import { Card } from "@/src/components/wrappers/Card";
import { formatDateUTC } from "@/src/helpers/formatDateUTC";
import { AddBookingPayment } from "../../AddBookingPayment/AddBookingPayment";
import { EditBookingPayment } from "../../EditBookingPayment/EditBookingPayment";
import RemoveBookingPayment from "../../RemoveBookingPayment/RemoveBookingPayment";
import BookingPaymentLogs from "../../BookingPaymentLogs/BookingPaymentLogs";
import { PaymentEmailDialog } from "../../PaymentEmailDialog";
import { PaymentEmailLog, PaymentEmailRow } from "../../PaymentEmailLog";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

const CustomerPaymentSection = ({
  customerPaymentData,
  currencyCode,
  bookingId,
  paymentData,
  emails,
  emailsLoading,
}: {
  customerPaymentData: any;
  currencyCode: string;
  bookingId: string;
  paymentData: {
    bookingAmount: number;
    paidSoFar: number;
  };
  emails?: Record<string, PaymentEmailRow[]>;
  emailsLoading?: boolean;
}) => {
  const formatPrice = (price: number, toCurrency: string) => {
    const formattedNumber = new Intl.NumberFormat("en-EN", { style: "currency", currency: toCurrency }).format(price);
    return `${formattedNumber}`;
  };
  // Receipt email per payment (ops' booking receipts): editable, to the customer or yourself.
  const [receiptFor, setReceiptFor] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const statusBackgroundColor = (status: string) => {
    switch (status) {
      case "APPROVED":
        return "bg-green-100";
      case "DECLINED":
        return "bg-red-100";
      case "PENDING":
        return "bg-yellow-100";
      case "ERROR":
        return "bg-red-100";
      case "CANCELED":
        return "bg-red-100";
      case "REFUNDED":
        return "bg-yellow-100";
      default:
        return "bg-blue-100";
    }
  };

  return (
    <Card className="w-full flex flex-col gap-y-6">
      <div className="flex flex-col">
        <div className="flex items-center">
          <h2 className="text-xl font-semibold">Payments from Customer</h2>
          <AddBookingPayment
            bookingId={bookingId}
            payerType="Customer"
            paymentData={paymentData}
            bookingCurrency={customerPaymentData?.[0]?.booking?.currency?.code || currencyCode}
          />
        </div>

        {!customerPaymentData?.length && <p className="text-sm font-regular text-slate-700">No payments yet.</p>}
        {customerPaymentData.map((payment: any, index: number) => (
          <div key={payment.id}>
            <div className="flex items-center">
              <p
                className={`flex float-left items-center text-xs font-medium pt-1 pb-1 pl-2 pr-2 mr-auto ${statusBackgroundColor(payment?.status)} rounded-3xl h-7 text-slate-900`}
              >
                {payment?.status[0].toUpperCase() + payment?.status.toLowerCase().slice(1)}
              </p>
              <div className="flex items-center gap-.5">
                <EditBookingPayment
                  initData={payment}
                  bookingId={bookingId}
                  payerType="Customer"
                  paymentData={paymentData}
                />
                <span className="flex items-center text-neutral-300">|</span>
                <RemoveBookingPayment paymentId={payment.id} bookingId={bookingId} />
                <span className="flex items-center text-neutral-300">|</span>
                <BookingPaymentLogs paymentId={payment.id} bookingId={bookingId} />
                <span className="flex items-center text-neutral-300">|</span>
                {payment?.status === "APPROVED" && (
                  <p className="text-sm text-blue-600 font-medium cursor-pointer pl-2 pt-4 pb-4" title="Email a receipt for this payment to the customer (or to yourself)" onClick={() => setReceiptFor(payment.id)}>Receipt</p>
                )}
              </div>
            </div>
            <div className="flex w-full">
              <p className="text-sm font-medium">Payment {customerPaymentData.length - index}</p>
              <p className="text-sm font-medium ml-auto">{formatPrice(+payment?.amount, currencyCode)}</p>
            </div>
            <p className="text-sm font-regular text-slate-700 mt-1">
              {formatDateUTC(payment?.paymentDate, "UTC", undefined, undefined, undefined, true)}
            </p>
            <p className="text-sm font-regular text-slate-700 mt-1">
              {payment?.paymentType === "cc_card" ? (
                <>
                  {payment?.paymentDetails?.cardCompany || "N/A"} {payment?.paymentDetails?.cardNumber || "N/A"} •{" "}
                  {payment?.paymentDetails?.nameOnCard || "N/A"}
                </>
              ) : payment?.paymentType === "Bank transfer" && payment?.bankAccount?.name ? (
                <>
                  <p>{payment?.paymentType}</p>
                  <p>
                    {" "}
                    {payment?.bankAccount?.name || "N/A"} • {payment?.bankAccount?.number || "N/A"}
                  </p>
                </>
              ) : (
                <>{payment?.paymentType || "N/A"}</>
              )}
            </p>
            <PaymentEmailLog rows={emails?.[payment.id]} loading={emailsLoading} />
            {index + 1 < customerPaymentData?.length && (
              <div className="w-full h-px flex justify-end bg-blue-100 mt-4 mb-2" />
            )}
          </div>
        ))}
      </div>
      {receiptFor && (
        <PaymentEmailDialog bookingId={bookingId} paymentId={receiptFor} kind="receipt" onClose={() => setReceiptFor(null)} onSent={() => { queryClient.invalidateQueries({ queryKey: ["paymentsData", bookingId] }); queryClient.invalidateQueries({ queryKey: ["paymentEmails", bookingId] }); }} />
      )}
    </Card>
  );
};

export default CustomerPaymentSection;
